import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'wouter';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  CalendarDays,
  CalendarPlus,
  ChevronLeft,
  ChevronRight,
  Grid3X3,
  List,
  Mail,
  Plus,
  Search,
  Sparkles,
  X,
} from 'lucide-react';
import type { DayContentProps } from 'react-day-picker';
import type { CalendarListResponseDto } from '@shared/api-contracts';

import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { PublicShell } from '@/components/public-shell';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useRegionScope } from '@/hooks/useRegionScope';
import {
  buildBrowseHref,
  readCanonicalBrowseState,
  updateBrowseUrl,
  useBrowseScrollRestoration,
} from '@/lib/browse-state';
import { cn } from '@/lib/utils';
import { apiRequest, fetchJson } from '@/lib/queryClient';
import { authedJson } from '@/lib/http';
import { AutoRedirect } from '@/components/auto-redirect';
import {
  CATEGORY_DOT,
  CATEGORY_LABELS,
  EntryCard,
  formatGroupHeader,
  getEntryDayKey,
  type CalendarEntry,
} from '@/components/calendar/entry-card';
import { EventDetailSheet } from '@/components/calendar/event-detail-sheet';

const DEFAULT_TIMEZONE = 'America/Indiana/Indianapolis';

type ScopeMode = 'today' | 'week' | 'weekend' | 'month';
type DisplayMode = 'calendar' | 'list' | 'grid';

const SCOPE_OPTIONS: { key: ScopeMode; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'Week' },
  { key: 'weekend', label: 'Weekend' },
  { key: 'month', label: 'Month' },
];

const DISPLAY_OPTIONS: { key: DisplayMode; label: string; icon: typeof CalendarDays }[] = [
  { key: 'calendar', label: 'Calendar', icon: CalendarDays },
  { key: 'list', label: 'List', icon: List },
  { key: 'grid', label: 'Grid', icon: Grid3X3 },
];

type CalendarListResponse = CalendarListResponseDto;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function parseDateParam(raw: string | undefined): Date | undefined {
  if (!raw || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return undefined;
  const [y, m, d] = raw.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function parseMonthParam(raw: string | undefined): Date | null {
  if (!raw || !/^\d{4}-\d{2}$/.test(raw)) return null;
  const [y, m] = raw.split('-').map(Number);
  return new Date(y, m - 1, 1);
}

function toMonthStr(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function parseScopeMode(raw: string | undefined): ScopeMode {
  if (raw === 'day' || raw === 'today') return 'today';
  return raw === 'week' || raw === 'weekend' || raw === 'month' ? raw : 'month';
}

function parseDisplayMode(raw: string | undefined): DisplayMode {
  return raw === 'calendar' || raw === 'list' || raw === 'grid' ? raw : 'calendar';
}

function toDateStr(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

function dateFromStr(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function addDays(dateStr: string, days: number): string {
  const date = dateFromStr(dateStr);
  date.setDate(date.getDate() + days);
  return toDateStr(date);
}

function compareDateStr(left: string, right: string): number {
  return left.localeCompare(right);
}

function startOfWeek(dateStr: string): string {
  const date = dateFromStr(dateStr);
  const dow = date.getDay();
  const mondayOffset = dow === 0 ? -6 : 1 - dow;
  date.setDate(date.getDate() + mondayOffset);
  return toDateStr(date);
}

function endOfWeek(dateStr: string): string {
  return addDays(startOfWeek(dateStr), 6);
}

function weekendRange(dateStr: string): { from: string; to: string } {
  const date = dateFromStr(dateStr);
  const dow = date.getDay();
  const fridayOffset = dow === 0 ? -2 : dow === 5 ? 0 : dow === 6 ? -1 : 5 - dow;
  const friday = toDateStr(
    new Date(date.getFullYear(), date.getMonth(), date.getDate() + fridayOffset)
  );
  return { from: friday, to: addDays(friday, 2) };
}

function dateRange(from: string, to: string): string[] {
  const days: string[] = [];
  for (let current = from; compareDateStr(current, to) <= 0; current = addDays(current, 1)) {
    days.push(current);
  }
  return days;
}

function startOfMonth(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;
}

function endOfMonth(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
  ).padStart(2, '0')}`;
}

function getEntryTimeLabel(entry: CalendarEntry): string {
  return new Date(entry.startsAt).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: entry.timezone,
  });
}

function shortCategoryLabel(category: string | undefined): string {
  if (!category) return 'Events';
  if (category === 'class_session') return 'Classes';
  if (category === 'farmers_market') return 'Markets';
  return CATEGORY_LABELS[category] ?? 'Events';
}

function getAgendaScope({
  scope,
  selectedDateStr,
  visibleMonth,
  timezone,
}: {
  scope: ScopeMode;
  selectedDateStr: string | undefined;
  visibleMonth: Date;
  timezone: string;
}): { from: string; to: string; queryTo: string; label: string; emptyLabel: string } {
  const today = new Date().toLocaleDateString('en-CA', { timeZone: timezone });
  const anchor = selectedDateStr ?? today;

  if (scope === 'today') {
    return {
      from: anchor,
      to: anchor,
      queryTo: addDays(anchor, 1),
      label: formatGroupHeader(anchor, timezone),
      emptyLabel: `Nothing scheduled for ${dateFromStr(anchor).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
      })}`,
    };
  }

  if (scope === 'weekend') {
    const rawRange = weekendRange(anchor);
    return {
      from: rawRange.from,
      to: rawRange.to,
      queryTo: addDays(rawRange.to, 1),
      label: `Agenda for ${dateFromStr(rawRange.from).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })} - ${dateFromStr(rawRange.to).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })}`,
      emptyLabel: 'Nothing scheduled this weekend',
    };
  }

  if (scope === 'week') {
    const rawFrom = startOfWeek(anchor);
    const rawTo = endOfWeek(anchor);
    return {
      from: rawFrom,
      to: rawTo,
      queryTo: addDays(rawTo, 1),
      label: `Agenda for ${dateFromStr(rawFrom).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })} - ${dateFromStr(rawTo).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })}`,
      emptyLabel: 'Nothing scheduled this week',
    };
  }

  const monthStart = startOfMonth(visibleMonth);
  const monthEnd = endOfMonth(visibleMonth);
  const from =
    selectedDateStr ??
    (compareDateStr(monthStart, today) <= 0 && compareDateStr(today, monthEnd) <= 0
      ? today
      : monthStart);
  return {
    from,
    to: monthEnd,
    queryTo: addDays(monthEnd, 1),
    label: selectedDateStr
      ? `Agenda from ${dateFromStr(selectedDateStr).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        })}`
      : `Agenda for ${visibleMonth.toLocaleDateString('en-US', {
          month: 'long',
          year: 'numeric',
        })}`,
    emptyLabel: 'Nothing scheduled this month',
  };
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function RegionCalendar() {
  const { regionSlug, localitySlug } = useParams<{ regionSlug: string; localitySlug?: string }>();
  const { region, locality, isLoading, isError } = useRegionScope(regionSlug, localitySlug);
  const { isAuthenticated, user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const initialBrowseState = useMemo(() => readCanonicalBrowseState(window.location.search), []);

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(() =>
    parseDateParam(initialBrowseState.date)
  );
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [scope, setScope] = useState<ScopeMode>(() => parseScopeMode(initialBrowseState.view));
  const [displayMode, setDisplayMode] = useState<DisplayMode>(() =>
    parseDisplayMode(initialBrowseState.display)
  );
  const [visibleMonth, setVisibleMonth] = useState<Date>(() => {
    const fromUrl = parseMonthParam(initialBrowseState.month);
    return fromUrl ?? parseDateParam(initialBrowseState.date) ?? new Date();
  });
  const [category, setCategory] = useState<string>(initialBrowseState.category ?? '');
  const [searchInput, setSearchInput] = useState<string>(initialBrowseState.q ?? '');
  const [debouncedQ, setDebouncedQ] = useState<string>(initialBrowseState.q ?? '');
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [openEventId, setOpenEventId] = useState<string | null>(
    new URLSearchParams(window.location.search).get('event')
  );

  // Digest subscribe form state
  const [digestEmail, setDigestEmail] = useState(() => '');
  const [digestName, setDigestName] = useState('');
  const [digestSuccess, setDigestSuccess] = useState(false);
  const [digestDismissed, setDigestDismissed] = useState(
    () => !!localStorage.getItem('cal-digest-dismissed')
  );

  // Pre-fill digest email from auth
  useEffect(() => {
    if (user?.email && !digestEmail) setDigestEmail(user.email);
    if (user?.firstName && !digestName)
      setDigestName(`${user.firstName}${user.lastName ? ' ' + user.lastName : ''}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Debounce search input → debouncedQ
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setDebouncedQ(searchInput), 400);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchInput]);

  const scopeTimezone = locality?.timezone ?? region?.timezone ?? DEFAULT_TIMEZONE;

  const apiBase =
    region && locality
      ? `/api/regions/${region.slug}/${locality.slug}`
      : region
        ? `/api/regions/${region.slug}`
        : '/api/regions';

  const basePath =
    region && locality
      ? `/regions/${region.slug}/${locality.slug}`
      : region
        ? `/regions/${region.slug}`
        : '/';

  const icsHref =
    region && locality
      ? `/api/calendar/feed/regions/${region.slug}/${locality.slug}.ics`
      : region
        ? `/api/calendar/feed/regions/${region.slug}.ics`
        : null;

  const selectedDateStr = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : undefined;
  const visibleMonthStr = toMonthStr(visibleMonth);
  const agendaScope = useMemo(
    () =>
      getAgendaScope({
        scope,
        selectedDateStr,
        visibleMonth,
        timezone: scopeTimezone,
      }),
    [scopeTimezone, selectedDateStr, scope, visibleMonth]
  );

  const browseState = useMemo(
    () => ({
      date: selectedDateStr,
      view: scope,
      display: displayMode,
      month: visibleMonthStr,
      category: category || undefined,
      q: debouncedQ || undefined,
    }),
    [selectedDateStr, scope, displayMode, visibleMonthStr, category, debouncedQ]
  );

  useEffect(() => {
    if (region) updateBrowseUrl(window.location.pathname, browseState);
  }, [browseState, region]);

  const currentSearch = buildBrowseHref(window.location.pathname, browseState).split('?')[1] ?? '';
  useBrowseScrollRestoration(
    window.location.pathname,
    currentSearch ? `?${currentSearch}` : '',
    !isLoading
  );

  // Agenda query — list and grid views
  const agendaQueryKey = [
    'region-calendar-agenda',
    region?.slug,
    locality?.slug,
    scope,
    agendaScope.from,
    agendaScope.to,
    category,
    debouncedQ,
  ];
  const { data: agendaResponse, isLoading: agendaLoading } = useQuery<CalendarListResponse>({
    queryKey: agendaQueryKey,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (scope === 'today' && selectedDateStr) {
        params.set('date', selectedDateStr);
      } else {
        params.set('dateFrom', agendaScope.from);
        params.set('dateTo', agendaScope.queryTo);
      }
      if (category) params.set('category', category);
      if (debouncedQ) params.set('q', debouncedQ);
      params.set('limit', '100');
      return authedJson<CalendarListResponse>(`${apiBase}/calendar?${params.toString()}`);
    },
    enabled: !!region,
    staleTime: 1000 * 60 * 5,
  });

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async ({ eventId, saved }: { eventId: string; saved: boolean }) => {
      await apiRequest(saved ? 'DELETE' : 'POST', `/api/calendar/events/${eventId}/save`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: agendaQueryKey }),
  });

  // RSVP mutation
  const rsvpMutation = useMutation({
    mutationFn: async ({
      eventId,
      status,
    }: {
      eventId: string;
      status: 'going' | 'interested' | null;
    }) => {
      await apiRequest(
        status === null ? 'DELETE' : 'POST',
        `/api/calendar/events/${eventId}/rsvp`,
        status !== null ? { status } : undefined
      );
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: agendaQueryKey }),
  });

  // Digest subscribe mutation
  const digestMutation = useMutation({
    mutationFn: async ({ email, name }: { email: string; name: string }) => {
      await apiRequest('POST', '/api/calendar/digest/subscribe', {
        email,
        name: name || undefined,
        localitySlug: locality?.slug,
      });
    },
    onSuccess: () => setDigestSuccess(true),
    onError: () =>
      toast({
        title: 'Could not subscribe',
        description: 'Please try again.',
        variant: 'destructive',
      }),
  });

  function handleSaveToggle(rawEventId: string) {
    if (!isAuthenticated) {
      toast({
        title: 'Log in to save events',
        description: 'Create an account to bookmark events and get reminders.',
      });
      return;
    }
    const currentlySaved = agendaResponse?.savedEventIds?.includes(rawEventId) ?? false;
    saveMutation.mutate({ eventId: rawEventId, saved: currentlySaved });
  }

  function handleRsvp(rawEventId: string, status: 'going' | 'interested') {
    if (!isAuthenticated) {
      toast({
        title: 'Log in to RSVP',
        description: 'Create an account to mark events you plan to attend.',
      });
      return;
    }
    rsvpMutation.mutate({ eventId: rawEventId, status });
  }

  function handleCancelRsvp(rawEventId: string) {
    rsvpMutation.mutate({ eventId: rawEventId, status: null });
  }

  // Featured strip query — only when no narrow filter is active
  const showFeatured = !category && !debouncedQ && !selectedDateStr;
  const { data: featuredResponse } = useQuery<{ data: CalendarEntry[] }>({
    queryKey: ['region-calendar-featured', region?.slug, locality?.slug],
    queryFn: async () => {
      const params = new URLSearchParams({ featured: 'true', limit: '6' });
      return fetchJson<{ data: CalendarEntry[] }>(`${apiBase}/calendar?${params.toString()}`);
    },
    enabled: !!region && showFeatured,
    staleTime: 1000 * 60 * 10,
  });
  const featuredEntries = showFeatured ? (featuredResponse?.data ?? []) : [];

  // Sync ?event= URL param when openEventId changes
  useEffect(() => {
    const url = new URL(window.location.href);
    if (openEventId) {
      url.searchParams.set('event', openEventId);
    } else {
      url.searchParams.delete('event');
    }
    window.history.replaceState(window.history.state, '', url.toString());
  }, [openEventId]);

  // Month query — powers the visible month grid for all Day/Week/Month scopes.
  const { data: monthResponse, isLoading: monthLoading } = useQuery<{ data: CalendarEntry[] }>({
    queryKey: [
      'region-calendar-month',
      region?.slug,
      locality?.slug,
      visibleMonthStr,
      category,
      debouncedQ,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({ month: visibleMonthStr, limit: '250' });
      if (category) params.set('category', category);
      if (debouncedQ) params.set('q', debouncedQ);
      return fetchJson<{ data: CalendarEntry[] }>(`${apiBase}/calendar?${params.toString()}`);
    },
    enabled: !!region,
    staleTime: 1000 * 60 * 5,
  });

  const entriesByDay = useMemo(() => {
    const map = new Map<string, CalendarEntry[]>();
    for (const entry of monthResponse?.data ?? []) {
      const dayKey = getEntryDayKey(entry);
      const bucket = map.get(dayKey) ?? [];
      bucket.push(entry);
      map.set(dayKey, bucket);
    }
    for (const entries of map.values()) {
      entries.sort((left, right) => left.startsAt.localeCompare(right.startsAt));
    }
    return map;
  }, [monthResponse]);

  const DayContentWithEntries = useCallback(
    ({ date }: { date: Date }) => {
      const dateStr = date.toLocaleDateString('en-CA', { timeZone: scopeTimezone });
      const entries = entriesByDay.get(dateStr) ?? [];
      const isDense = entries.length > 3;
      const categoryCounts = entries.reduce<Record<string, { label: string; count: number }>>(
        (acc, entry) => {
          const cat = entry.category ?? 'other';
          if (!acc[cat]) acc[cat] = { label: shortCategoryLabel(entry.category), count: 0 };
          acc[cat].count++;
          return acc;
        },
        {}
      );
      const categorySummary = Object.entries(categoryCounts)
        .sort((l, r) => r[1].count - l[1].count)
        .slice(0, 2);

      return (
        <div className="flex h-full flex-col items-stretch overflow-hidden px-1 py-1 text-left">
          <span className="text-sm font-medium leading-none">{date.getDate()}</span>

          {/* ── Mobile: colored dots ─────────────────────────── */}
          {entries.length > 0 && (
            <div className="sm:hidden mt-1.5 flex flex-wrap items-center gap-[3px]">
              {entries.slice(0, 5).map(entry => (
                <div
                  key={entry.id}
                  className={cn(
                    'h-[7px] w-[7px] rounded-full shrink-0',
                    CATEGORY_DOT[entry.category ?? 'other'] ?? 'bg-muted-foreground'
                  )}
                />
              ))}
              {entries.length > 5 && (
                <span className="text-[9px] font-semibold leading-none text-muted-foreground">
                  +{entries.length - 5}
                </span>
              )}
            </div>
          )}

          {/* ── Desktop: text pills ──────────────────────────── */}
          {entries.length === 0 ? null : isDense ? (
            <div className="hidden sm:block mt-1 min-w-0 space-y-0.5">
              {categorySummary.map(([cat, { label, count }]) => (
                <span
                  key={cat}
                  className="flex min-w-0 items-center gap-1 truncate rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium leading-tight text-foreground"
                >
                  <span
                    className={cn(
                      'h-1.5 w-1.5 shrink-0 rounded-full',
                      CATEGORY_DOT[cat] ?? 'bg-muted-foreground'
                    )}
                  />
                  <span className="truncate">
                    {count} {label}
                  </span>
                </span>
              ))}
              <span className="block truncate text-[10px] font-medium leading-tight text-muted-foreground">
                {entries.length} total
              </span>
            </div>
          ) : (
            <div className="hidden sm:block mt-1 min-w-0 space-y-0.5">
              {entries.slice(0, 3).map(entry => (
                <span
                  key={entry.id}
                  className="flex min-w-0 items-center gap-1 truncate rounded-md bg-muted px-1.5 py-0.5 text-[10px] leading-tight text-foreground"
                >
                  <span
                    className={cn(
                      'h-1.5 w-1.5 shrink-0 rounded-full',
                      CATEGORY_DOT[entry.category ?? 'other'] ?? 'bg-muted-foreground'
                    )}
                  />
                  <span className="truncate">
                    <span className="font-medium">{getEntryTimeLabel(entry)}</span> {entry.title}
                  </span>
                </span>
              ))}
            </div>
          )}
        </div>
      );
    },
    [entriesByDay, scopeTimezone]
  );

  function handleSelectDate(date: Date | undefined) {
    setSelectedDate(date);
    // Preserve the active timeframe; date selection only re-anchors the agenda.
    if (date) setVisibleMonth(new Date(date.getFullYear(), date.getMonth(), 1));
  }

  function clearDateFilter() {
    setSelectedDate(undefined);
    setScope('month');
  }

  function handleNavigatePeriod(direction: -1 | 1) {
    if (scope === 'month') {
      setVisibleMonth(
        current => new Date(current.getFullYear(), current.getMonth() + direction, 1)
      );
      setSelectedDate(undefined);
      return;
    }

    const anchor =
      selectedDateStr ?? new Date().toLocaleDateString('en-CA', { timeZone: scopeTimezone });
    const dayDelta = scope === 'today' ? direction : direction * 7;
    const nextDate = dateFromStr(addDays(anchor, dayDelta));
    setSelectedDate(nextDate);
    setVisibleMonth(new Date(nextDate.getFullYear(), nextDate.getMonth(), 1));
  }

  // ─── Loading / error states ────────────────────────────────────────────────

  if (isLoading) {
    return (
      <PublicShell routeKey={localitySlug ? 'locality-calendar' : 'region-calendar'}>
        <div className="page-shell flex min-h-[60vh] items-center justify-center">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      </PublicShell>
    );
  }

  if (isError || !region) {
    return (
      <PublicShell routeKey="not-found">
        <div className="page-shell-narrow flex min-h-[60vh] flex-col items-center justify-center gap-4 text-center">
          <h1 className="text-4xl font-semibold">This calendar is unavailable.</h1>
          <AutoRedirect />
          <Link href="/">
            <Button className="rounded-full px-5">Choose another market</Button>
          </Link>
        </div>
      </PublicShell>
    );
  }

  const displayName = locality?.name ?? region.name;
  const routeKey = locality ? 'locality-calendar' : 'region-calendar';
  const calendarLoading =
    agendaLoading || (displayMode === 'calendar' && scope === 'month' && monthLoading);

  // Group agenda entries by local day for list/grid views
  const agendaEntries = agendaResponse?.data ?? [];
  const savedIds = agendaResponse?.savedEventIds ?? [];
  const rsvpCounts = agendaResponse?.rsvpCounts ?? {};
  const userRsvpMap = agendaResponse?.userRsvpMap ?? {};

  // Strip "event-" prefix to get the raw DB UUID for API calls
  function rawId(entryId: string): string {
    return entryId.replace(/^event-/, '');
  }
  const groupedEntries = (() => {
    const groups = new Map<string, CalendarEntry[]>();
    for (const entry of agendaEntries) {
      const dayKey = getEntryDayKey(entry);
      if (!groups.has(dayKey)) groups.set(dayKey, []);
      const bucket = groups.get(dayKey);
      if (bucket) bucket.push(entry);
    }
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
  })();
  const agendaEntriesByDay = new Map(groupedEntries);
  const periodDays = scope === 'month' ? [] : dateRange(agendaScope.from, agendaScope.to);

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <PublicShell routeKey={routeKey}>
      {/* Hero */}
      <section className="hero-gradient border-b border-border/60">
        <div className="page-shell py-12">
          <p className="editorial-kicker">What's happening</p>
          <h1 className="section-title mt-4">
            {region.type === 'online'
              ? 'See what is live next.'
              : `${displayName} Events & Classes`}
          </h1>
          <p className="section-copy mt-5 max-w-2xl">
            Everything happening in {displayName} — classes, markets, community events, and local
            gatherings.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            {icsHref ? (
              <a href={icsHref} title="Subscribe to this calendar in your calendar app">
                <Button variant="outline" className="rounded-full px-5 gap-2">
                  <CalendarPlus className="h-4 w-4" />
                  Subscribe to calendar
                </Button>
              </a>
            ) : null}
            <Link href={`${basePath}/calendar/submit`}>
              <Button variant="outline" className="rounded-full px-5 gap-2">
                <Plus className="h-4 w-4" />
                Submit an event
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {featuredEntries.length > 0 && (
        <section className="border-b border-border/60 bg-muted/30 py-8">
          <div className="page-shell space-y-4">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              <p className="editorial-kicker">Featured</p>
            </div>
            <div className="flex gap-4 overflow-x-auto overscroll-x-contain pb-2 -mx-4 px-4 md:-mx-0 md:px-0">
              {featuredEntries.map(entry => (
                <div key={entry.id} className="w-72 shrink-0">
                  <EntryCard
                    entry={entry}
                    basePath={basePath}
                    onOpen={() => setOpenEventId(entry.id)}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="page-section">
        <div className="page-shell space-y-5">
          {/* Controls bar */}
          <div className="flex flex-col gap-3 overflow-x-hidden rounded-[1.75rem] border border-border/70 bg-card p-4 shadow-sm">
            {/* Row 1: date picker */}
            <div className="flex flex-wrap items-center gap-2">
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={`rounded-full px-4 gap-2 ${selectedDate ? 'border-primary text-primary' : ''}`}
                  >
                    <CalendarDays className="h-4 w-4" />
                    {selectedDate ? format(selectedDate, 'MMM d, yyyy') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={date => {
                      handleSelectDate(date);
                      setCalendarOpen(false);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <p className="text-xs text-muted-foreground">
                {selectedDate ? 'Using selected date as the anchor.' : 'Showing upcoming events.'}
              </p>
            </div>

            {/* Row 1b: Day / Week / Weekend / Month scope toggle — scrolls at narrow widths */}
            <div className="overflow-x-auto overscroll-x-contain -mx-1 px-1">
              <div className="inline-flex rounded-full border border-border/70 bg-muted/50 p-1 shrink-0">
                {SCOPE_OPTIONS.map(option => (
                  <button
                    key={option.key}
                    type="button"
                    aria-label={`${option.label} agenda`}
                    onClick={() => setScope(option.key)}
                    className={`touch-target rounded-full px-4 text-sm font-medium ${
                      scope === option.key ? 'bg-background shadow-sm' : ''
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Display
              </p>
              <div className="inline-flex rounded-full border border-border/70 bg-muted/50 p-1 shrink-0">
                {DISPLAY_OPTIONS.map(option => {
                  const Icon = option.icon;
                  return (
                    <button
                      key={option.key}
                      type="button"
                      aria-label={`${option.label} display`}
                      onClick={() => setDisplayMode(option.key)}
                      className={`touch-target rounded-full px-3 text-sm font-medium inline-flex items-center gap-1.5 ${
                        displayMode === option.key ? 'bg-background shadow-sm' : ''
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="hidden sm:inline">{option.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border/60 bg-muted/25 px-3 py-2">
              <Button
                type="button"
                variant="ghost"
                className="rounded-full gap-1 px-3"
                onClick={() => handleNavigatePeriod(-1)}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <p className="min-w-0 flex-1 text-center text-sm font-medium">{agendaScope.label}</p>
              <Button
                type="button"
                variant="ghost"
                className="rounded-full gap-1 px-3"
                onClick={() => handleNavigatePeriod(1)}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            {selectedDate && (
              <div>
                <button
                  type="button"
                  onClick={clearDateFilter}
                  className="inline-flex items-center gap-1 rounded-full border border-border/70 bg-muted/40 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-accent transition-colors"
                >
                  <X className="h-3 w-3" />
                  Show full month agenda
                </button>
              </div>
            )}

            {/* Row 3: category chips — scroll on mobile, wrap on wider/portrait screens */}
            <div className="flex gap-2 overflow-x-auto overscroll-x-contain pb-1 -mb-1 scrollbar-none sm:flex-wrap sm:overflow-visible sm:pb-0">
              {(['', ...Object.keys(CATEGORY_LABELS)] as string[]).map(cat => (
                <button
                  key={cat || 'all'}
                  type="button"
                  onClick={() => setCategory(cat)}
                  className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                    category === cat
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border/70 bg-muted/40 text-foreground hover:bg-accent'
                  }`}
                >
                  {cat ? (
                    <span
                      className={`h-2 w-2 shrink-0 rounded-full ${CATEGORY_DOT[cat] ?? 'bg-muted-foreground'} ${category === cat ? 'ring-1 ring-primary-foreground/70' : ''}`}
                    />
                  ) : null}
                  {cat ? CATEGORY_LABELS[cat] : 'All'}
                </button>
              ))}
            </div>

            {/* Row 3: search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                type="search"
                placeholder="Search events and classes…"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                className="pl-9 rounded-full"
              />
              {searchInput && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => {
                    setSearchInput('');
                    setDebouncedQ('');
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          <div className="space-y-6">
            {displayMode === 'calendar' && scope === 'month' && (
              <Card className="rounded-[1.75rem] border-border/70 shadow-sm overflow-hidden">
                <CardContent className="p-3 sm:p-5">
                  {monthLoading ? (
                    <div className="h-[36rem] animate-pulse rounded-xl bg-muted" />
                  ) : (
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={handleSelectDate}
                      month={visibleMonth}
                      onMonthChange={setVisibleMonth}
                      className="w-full p-0"
                      classNames={{
                        months: 'w-full',
                        month: 'w-full space-y-3',
                        caption: 'flex justify-center pt-1 relative items-center mb-2',
                        caption_label: 'text-base font-semibold',
                        nav_button:
                          'h-8 w-8 bg-transparent p-0 opacity-70 hover:opacity-100 border border-border rounded-md inline-flex items-center justify-center',
                        nav_button_previous: 'absolute left-1',
                        nav_button_next: 'absolute right-1',
                        table: 'w-full border-collapse',
                        head_row: 'grid w-full grid-cols-7',
                        head_cell:
                          'text-muted-foreground font-normal text-[0.65rem] sm:text-xs text-center py-2 uppercase tracking-wide',
                        row: 'grid w-full grid-cols-7 gap-1 mt-1',
                        cell: 'min-w-0 text-center p-0',
                        day: 'h-14 sm:h-28 lg:h-32 w-full rounded-xl border border-border/50 bg-background p-0 font-normal aria-selected:opacity-100 hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors',
                        day_selected:
                          'border-primary bg-primary/10 text-foreground hover:bg-primary/15 hover:text-foreground',
                        day_today:
                          'border-primary/60 bg-accent text-accent-foreground font-semibold',
                        day_outside: 'text-muted-foreground opacity-35',
                      }}
                      components={{
                        DayContent: DayContentWithEntries as unknown as (
                          props: DayContentProps
                        ) => React.ReactElement,
                      }}
                    />
                  )}
                </CardContent>
              </Card>
            )}

            {displayMode === 'calendar' && scope !== 'month' && (
              <Card className="rounded-[1.75rem] border-border/70 shadow-sm overflow-hidden">
                <CardContent className="p-3 sm:p-5">
                  {agendaLoading ? (
                    <div className="h-56 animate-pulse rounded-xl bg-muted" />
                  ) : (
                    <div
                      className={`grid gap-2 ${
                        periodDays.length <= 2 ? 'md:grid-cols-2' : 'md:grid-cols-7'
                      }`}
                    >
                      {periodDays.map(dayKey => {
                        const dayEntries = agendaEntriesByDay.get(dayKey) ?? [];
                        const dayDate = dateFromStr(dayKey);
                        return (
                          <button
                            key={dayKey}
                            type="button"
                            onClick={() => handleSelectDate(dayDate)}
                            className="min-h-44 rounded-xl border border-border/60 bg-background p-3 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            <span className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                              {dayDate.toLocaleDateString('en-US', { weekday: 'short' })}
                            </span>
                            <span className="mt-1 block text-2xl font-semibold">
                              {dayDate.getDate()}
                            </span>
                            {dayEntries.length === 0 ? (
                              <span className="mt-4 block text-xs text-muted-foreground">
                                No events
                              </span>
                            ) : (
                              <span className="mt-3 block space-y-1">
                                {dayEntries.slice(0, 4).map(entry => (
                                  <span
                                    key={entry.id}
                                    className="block truncate rounded-md bg-muted px-2 py-1 text-xs leading-tight"
                                  >
                                    <span className="font-medium">{getEntryTimeLabel(entry)}</span>{' '}
                                    {entry.title}
                                  </span>
                                ))}
                                {dayEntries.length > 4 ? (
                                  <span className="block text-xs font-medium text-muted-foreground">
                                    +{dayEntries.length - 4} more
                                  </span>
                                ) : null}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            <div>
              <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
                <div>
                  <p className="editorial-kicker">Agenda</p>
                  <h2 className="mt-1 text-2xl font-semibold">{agendaScope.label}</h2>
                </div>
                <p className="text-sm text-muted-foreground">
                  {agendaEntries.length} {agendaEntries.length === 1 ? 'item' : 'items'}
                </p>
              </div>

              {calendarLoading ? (
                <div className="grid gap-5">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div
                      key={i}
                      className="h-36 animate-pulse rounded-[1.75rem] border border-border/70 bg-card"
                    />
                  ))}
                </div>
              ) : groupedEntries.length === 0 ? (
                <Card className="rounded-[1.75rem] border-border/70 shadow-sm">
                  <CardContent className="space-y-4 p-6">
                    <div className="flex items-center gap-3">
                      <CalendarDays className="h-5 w-5 text-primary" />
                      <h2 className="text-2xl font-semibold">{agendaScope.emptyLabel}</h2>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      {selectedDate && (
                        <Button className="rounded-full px-5" onClick={clearDateFilter}>
                          Show month agenda
                        </Button>
                      )}
                      <Link href={`${basePath}/classes`}>
                        <Button variant="outline" className="rounded-full px-5">
                          Browse classes
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-8">
                  {groupedEntries.map(([dayKey, dayEntries]) => (
                    <div key={dayKey}>
                      <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">
                        {formatGroupHeader(dayKey, scopeTimezone)}
                      </h2>
                      <div
                        className={
                          displayMode === 'grid' ? 'grid gap-4 lg:grid-cols-2' : 'grid gap-4'
                        }
                      >
                        {dayEntries.map(entry => {
                          const rid = rawId(entry.id);
                          const counts = rsvpCounts[rid];
                          return (
                            <EntryCard
                              key={entry.id}
                              entry={entry}
                              basePath={basePath}
                              onOpen={() => setOpenEventId(entry.id)}
                              isSaved={savedIds.includes(rid)}
                              onSave={() => handleSaveToggle(rid)}
                              rsvpStatus={userRsvpMap[rid] ?? null}
                              onRsvp={status => handleRsvp(rid, status)}
                              onCancelRsvp={() => handleCancelRsvp(rid)}
                              goingCount={counts?.going ?? 0}
                              interestedCount={counts?.interested ?? 0}
                            />
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── Weekly digest subscribe card ─────────────────────── */}
            {locality && !digestDismissed && !digestSuccess && (
              <div className="rounded-[1.75rem] border border-border/70 bg-card shadow-sm p-6">
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold">Stay in the loop</p>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      Get a weekly digest of upcoming events in {locality.name} every Monday.
                    </p>
                    <form
                      className="mt-4 space-y-2"
                      onSubmit={e => {
                        e.preventDefault();
                        if (digestEmail)
                          digestMutation.mutate({ email: digestEmail, name: digestName });
                      }}
                    >
                      <input
                        type="email"
                        required
                        placeholder="your@email.com"
                        value={digestEmail}
                        onChange={e => setDigestEmail(e.target.value)}
                        className="w-full rounded-full border border-border/70 bg-background px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                      <input
                        type="text"
                        placeholder="Your name (optional)"
                        value={digestName}
                        onChange={e => setDigestName(e.target.value)}
                        className="w-full rounded-full border border-border/70 bg-background px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                      <Button
                        type="submit"
                        className="rounded-full px-5"
                        disabled={digestMutation.isPending}
                      >
                        Subscribe
                      </Button>
                    </form>
                  </div>
                  <button
                    type="button"
                    aria-label="Dismiss"
                    onClick={() => {
                      setDigestDismissed(true);
                      localStorage.setItem('cal-digest-dismissed', '1');
                    }}
                    className="text-muted-foreground hover:text-foreground shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            {locality && digestSuccess && (
              <div className="rounded-[1.75rem] border border-border/70 bg-card shadow-sm p-6 text-center">
                <p className="font-semibold text-primary">You're subscribed!</p>
                <p className="text-sm text-muted-foreground mt-1">
                  You'll receive weekly event updates for {locality.name}.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      <EventDetailSheet
        entryId={openEventId}
        onOpenChange={open => {
          if (!open) setOpenEventId(null);
        }}
      />
    </PublicShell>
  );
}
